import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Play,
  Loader2,
  Users,
  Timer,
  Zap,
  Activity,
  Clock,
  TrendingUp,
  StickyNote,
} from "lucide-react";
import { apiService } from "../../services/api.service";
import type {
  ClassroomRelationship,
  LoadTestConfig,
  LoadTestTargetPreview,
  TrafficPattern,
  StopMode,
} from "../../services/api.service";
import { LOAD_TEST_DEFAULTS } from "../../config/api.config";
import { TargetPreview } from "./TargetPreview";

interface LoadTestConfigFormProps {
  config: LoadTestConfig;
  isRunning: boolean;
  isLoading: boolean;
  notes: string;
  onConfigChange: (field: keyof LoadTestConfig, value: string | number) => void;
  onNotesChange: (notes: string) => void;
  onStartTest: () => void;
}

export const LoadTestConfigForm = ({
  config,
  isRunning,
  isLoading,
  notes,
  onConfigChange,
  onNotesChange,
  onStartTest,
}: LoadTestConfigFormProps) => {
  const [preview, setPreview] = useState<LoadTestTargetPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [classrooms, setClassrooms] = useState<ClassroomRelationship[]>([]);
  const [classroomsLoading, setClassroomsLoading] = useState(false);
  const [classroomsError, setClassroomsError] = useState<string | null>(null);

  // Load the classroom list once so the user can pick the dedicated load-test
  // classroom (its id is auto-assigned by the provision script and not stable).
  useEffect(() => {
    let cancelled = false;
    setClassroomsLoading(true);
    apiService
      .getClassroomRelationships(undefined, { include_students: false })
      .then((rows) => {
        if (cancelled) return;
        const sorted = [...rows].sort((a, b) =>
          a.classroom_name.localeCompare(b.classroom_name),
        );
        setClassrooms(sorted);
        setClassroomsError(null);
        // Pre-select the provisioned load-test classroom by name when nothing
        // is selected yet, so the form lands on the right target by default.
        if (!config.classroom_id) {
          const provisioned = sorted.find(
            (c) => c.classroom_name === LOAD_TEST_DEFAULTS.classroom_name,
          );
          if (provisioned) {
            onConfigChange("classroom_id", provisioned.classroom_id);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setClassroomsError("Failed to load classrooms");
      })
      .finally(() => {
        if (!cancelled) setClassroomsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Run once on mount; onConfigChange/config.classroom_id are intentionally
    // omitted so the default-selection only fires for the initial empty state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const classroomId = config.classroom_id;
    if (!classroomId || classroomId <= 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      // Show the spinner only once the debounce fires and a fetch actually
      // starts — not on every keystroke.
      setPreviewLoading(true);
      try {
        const result = await apiService.getLoadTestTargetPreview(classroomId);
        if (!cancelled) {
          setPreview(result);
          setPreviewError(null);
        }
      } catch {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(`Classroom ${classroomId} not found or unavailable`);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
      // Clear any in-flight spinner when the input changes again before the
      // fetch settled, so it can't stick.
      setPreviewLoading(false);
    };
  }, [config.classroom_id]);

  return (
    <Card className="max-w-2xl w-full">
      <CardHeader className="border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
            <Zap className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <CardTitle className="text-lg">New Load Test</CardTitle>
            <CardDescription>
              Configure concurrent client simulation
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        <div className="space-y-2">
          <Label
            htmlFor="num_clients"
            className="text-sm font-medium flex items-center gap-2"
          >
            <Users className="h-4 w-4 text-muted-foreground" />
            Concurrent Clients
          </Label>
          <Input
            id="num_clients"
            type="number"
            min={1}
            max={100}
            value={config.num_clients}
            onChange={(e) =>
              onConfigChange("num_clients", parseInt(e.target.value) || 1)
            }
            disabled={isRunning}
            className="h-10 text-center"
          />
          <p className="text-xs text-muted-foreground">Simulate 1-100 users</p>
        </div>

        <div className="space-y-3 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <Timer className="h-4 w-4 text-muted-foreground" />
              Stop Condition
            </p>
            <p className="text-xs text-muted-foreground">
              Run each client for a fixed number of turns, or for a fixed
              duration (taking turns until time runs out).
            </p>
          </div>

          <Select
            value={config.stop_mode}
            onValueChange={(value) =>
              onConfigChange("stop_mode", value as StopMode)
            }
            disabled={isRunning}
          >
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="turns">Stop after N turns</SelectItem>
              <SelectItem value="duration">Stop after a duration</SelectItem>
            </SelectContent>
          </Select>

          {config.stop_mode === "turns" ? (
            <div className="space-y-2">
              <Label htmlFor="turns" className="text-sm font-medium">
                Turns per Client
              </Label>
              <Input
                id="turns"
                type="number"
                min={1}
                max={20}
                value={config.turns}
                onChange={(e) =>
                  onConfigChange("turns", parseInt(e.target.value) || 1)
                }
                disabled={isRunning}
                className="h-10 text-center"
              />
              <p className="text-xs text-muted-foreground">
                Conversation rounds (1-20). Total API calls:{" "}
                <span className="font-semibold text-foreground">
                  {config.num_clients * config.turns}
                </span>
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label
                htmlFor="duration_seconds"
                className="text-sm font-medium flex items-center gap-2"
              >
                <Clock className="h-4 w-4 text-muted-foreground" />
                Duration (seconds)
              </Label>
              <Input
                id="duration_seconds"
                type="number"
                min={1}
                max={3600}
                value={config.duration_seconds}
                onChange={(e) =>
                  onConfigChange(
                    "duration_seconds",
                    parseInt(e.target.value) || 1,
                  )
                }
                disabled={isRunning}
                className="h-10 text-center"
              />
              <p className="text-xs text-muted-foreground">
                Each client keeps taking turns until this elapses (an in-flight
                turn finishes, so a run can overshoot by one turn).
              </p>
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Arrival Pattern
            </p>
            <p className="text-xs text-muted-foreground">
              Controls when each client starts. Clients wait for their slot
              before logging in, so the pattern survives container boot jitter.
            </p>
          </div>

          <Select
            value={config.traffic_pattern}
            onValueChange={(value) =>
              onConfigChange("traffic_pattern", value as TrafficPattern)
            }
            disabled={isRunning}
          >
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_at_once">
                All at once (thundering herd)
              </SelectItem>
              <SelectItem value="linear_ramp">
                Linear ramp (even over a window)
              </SelectItem>
              <SelectItem value="random_within_window">
                Random within a window
              </SelectItem>
              <SelectItem value="burst_groups">
                Burst groups (batches over time)
              </SelectItem>
            </SelectContent>
          </Select>

          <div className="space-y-2">
            <Label
              htmlFor="boot_buffer_seconds"
              className="text-sm font-medium"
            >
              Boot buffer (seconds)
            </Label>
            <Input
              id="boot_buffer_seconds"
              type="number"
              min={0}
              max={600}
              value={config.boot_buffer_seconds}
              onChange={(e) =>
                onConfigChange(
                  "boot_buffer_seconds",
                  parseInt(e.target.value) || 0,
                )
              }
              disabled={isRunning}
              className="h-10 text-center"
            />
            <p className="text-xs text-muted-foreground">
              Lead time before the schedule begins, so tasks finish booting
              first (default 90s). Tasks slower than this start as soon as they
              boot.
            </p>
          </div>

          {config.traffic_pattern !== "all_at_once" && (
            <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
              <div className="space-y-2">
                <Label
                  htmlFor="pattern_window_seconds"
                  className="text-sm font-medium"
                >
                  {config.traffic_pattern === "burst_groups"
                    ? "Seconds between bursts"
                    : "Window (seconds)"}
                </Label>
                <Input
                  id="pattern_window_seconds"
                  type="number"
                  min={0}
                  max={3600}
                  value={config.pattern_window_seconds}
                  onChange={(e) =>
                    onConfigChange(
                      "pattern_window_seconds",
                      parseInt(e.target.value) || 0,
                    )
                  }
                  disabled={isRunning}
                  className="h-10 text-center"
                />
              </div>

              {config.traffic_pattern === "burst_groups" && (
                <div className="space-y-2">
                  <Label htmlFor="batch_size" className="text-sm font-medium">
                    Clients per burst
                  </Label>
                  <Input
                    id="batch_size"
                    type="number"
                    min={1}
                    max={100}
                    value={config.batch_size}
                    onChange={(e) =>
                      onConfigChange("batch_size", parseInt(e.target.value) || 1)
                    }
                    disabled={isRunning}
                    className="h-10 text-center"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium text-foreground">Tutor Target</p>
            <p className="text-xs text-muted-foreground">
              Each client logs in as a student from this classroom and opens the
              lesson assigned to it.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="classroom_id" className="text-sm font-medium">
              Classroom
            </Label>
            <Select
              value={config.classroom_id ? String(config.classroom_id) : ""}
              onValueChange={(value) =>
                onConfigChange("classroom_id", parseInt(value, 10) || 0)
              }
              disabled={isRunning || classroomsLoading}
            >
              <SelectTrigger id="classroom_id" className="h-10">
                <SelectValue
                  placeholder={
                    classroomsLoading
                      ? "Loading classrooms..."
                      : "Select a classroom"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {classrooms.map((c) => (
                  <SelectItem
                    key={c.classroom_id}
                    value={String(c.classroom_id)}
                  >
                    {c.classroom_name} (#{c.classroom_id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {classroomsError && (
              <p className="text-xs text-destructive">{classroomsError}</p>
            )}
          </div>

          <TargetPreview
            preview={preview}
            isLoading={previewLoading}
            error={previewError}
            numClients={config.num_clients}
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="run_notes"
            className="text-sm font-medium flex items-center gap-2"
          >
            <StickyNote className="h-4 w-4 text-muted-foreground" />
            Notes <span className="text-destructive">*</span>
          </Label>
          <textarea
            id="run_notes"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            disabled={isRunning}
            rows={4}
            maxLength={20000}
            placeholder="Describe what this run is testing — purpose, hypothesis, config changes, what to watch for…"
            className="w-full resize-y rounded-md border border-border/60 bg-background p-3 text-sm shadow-sm outline-none focus:border-lime-600/40 focus:ring-1 focus:ring-lime-600/20 disabled:opacity-50"
          />
          <p className="text-xs text-muted-foreground">
            Required — note why you're running this test before you start it.
          </p>
        </div>

        <Button
          className="w-full h-10"
          size="lg"
          onClick={onStartTest}
          disabled={isRunning || isLoading || notes.trim().length === 0}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Initializing...
            </>
          ) : isRunning ? (
            <>
              <Activity className="h-5 w-5 mr-2 animate-pulse" />
              Test Running...
            </>
          ) : (
            <>
              <Play className="h-5 w-5 mr-2" />
              Start Load Test
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
